'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { Mic, Square, Send } from 'lucide-react';
import { API } from '@/lib/utils/api';

const MAX_ROWS = 5;

interface STTInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  placeholder?: string;
  disabled?: boolean;
  language?: 'en' | 'he';
}

export function STTInput({
  value,
  onChange,
  onSubmit,
  placeholder = '',
  disabled = false,
  language = 'en',
}: STTInputProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isTranscribingRef = useRef(false);

  const isHebrew = language === 'he';

  // Auto-resize textarea
  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';
    const lineHeight = parseInt(getComputedStyle(textarea).lineHeight) || 24;
    const maxHeight = lineHeight * MAX_ROWS;
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
  }, []);

  useEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4',
      });

      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());

        // Clear timer
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }

        // Transcribe
        await transcribeAudio();
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(100); // Collect data every 100ms

      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(t => t + 1);
      }, 1000);
    } catch (error) {
      console.error('Error starting recording:', error);
      alert(isHebrew ? 'לא ניתן לגשת למיקרופון' : 'Cannot access microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async () => {
    // Prevent duplicate transcription calls
    if (chunksRef.current.length === 0 || isTranscribingRef.current) return;

    isTranscribingRef.current = true;
    setIsTranscribing(true);

    // Capture chunks and clear immediately to prevent re-use
    const chunks = chunksRef.current;
    chunksRef.current = [];

    try {
      const audioBlob = new Blob(chunks, { type: 'audio/webm' });
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      formData.append('language', language);

      const response = await fetch(API.stt, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) throw new Error('Transcription failed');

      const data = await response.json();
      if (data.text) {
        // Append to existing text with space if needed
        const newText = value.trim()
          ? `${value.trimEnd()} ${data.text}`
          : data.text;
        onChange(newText);
      }
    } catch (error) {
      console.error('Error transcribing:', error);
    } finally {
      isTranscribingRef.current = false;
      setIsTranscribing(false);
      setRecordingTime(0);
    }
  };

  const handleToggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <div className="flex gap-2" dir={isHebrew ? 'rtl' : 'ltr'}>
      {/* Text Input */}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled || isRecording || isTranscribing}
        rows={1}
        className="input flex-1 resize-none overflow-y-auto"
      />

      {/* Send Button */}
      <button
        onClick={onSubmit}
        disabled={disabled || !value.trim() || isRecording || isTranscribing}
        className="btn btn-primary px-4"
      >
        <Send className="w-4 h-4" />
      </button>

      {/* Recording Button */}
      <button
        onClick={handleToggleRecording}
        disabled={disabled || isTranscribing}
        className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors min-w-[80px] ${
          isRecording
            ? 'bg-error text-white animate-pulse'
            : isTranscribing
            ? 'bg-muted text-muted-foreground'
            : 'bg-error/80 text-white hover:bg-error'
        }`}
      >
        {isTranscribing ? (
          <span className="text-sm">...</span>
        ) : isRecording ? (
          <>
            <Square className="w-4 h-4" />
            <span className="font-mono text-sm">{formatTime(recordingTime)}</span>
          </>
        ) : (
          <Mic className="w-4 h-4" />
        )}
      </button>
    </div>
  );
}
